import { useEffect, useMemo, useState } from "react";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Sparkles, Loader2, Plus, Trash2, Save, Wand2, ExternalLink } from "lucide-react";

const emptyDraft = {
  pdf_url: "",
  video_url: "",
  welcome_letter: "",
  instructor_intro: "",
  tech_checklist: [],
  key_dates: [],
  code_of_conduct: "",
  support_contacts: [],
};

const WelcomePackEditor = () => {
  const [programs, setPrograms] = useState([]);
  const [programId, setProgramId] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);

  // AI gen inputs
  const [cohortName, setCohortName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [instructorName, setInstructorName] = useState("");
  const [audienceNotes, setAudienceNotes] = useState("");
  const [tone, setTone] = useState("warm, encouraging, neurodiversity-affirming");
  const [extraInstructions, setExtraInstructions] = useState("");

  useEffect(() => {
    fetch(`${API}/programs`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.programs || [];
        setPrograms(list);
        if (list.length && !programId) setProgramId(list[0].program_id);
      })
      .catch(() => toast.error("Could not load programs"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!programId) return;
    setLoading(true);
    fetch(`${API}/programs/${programId}/welcome-packet`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        const wp = data?.welcome_packet || {};
        setDraft({
          pdf_url: wp.pdf_url || "",
          video_url: wp.video_url || "",
          welcome_letter: wp.welcome_letter || "",
          instructor_intro: wp.instructor_intro || "",
          tech_checklist: Array.isArray(wp.tech_checklist) ? wp.tech_checklist : [],
          key_dates: Array.isArray(wp.key_dates) ? wp.key_dates : [],
          code_of_conduct: wp.code_of_conduct || "",
          support_contacts: Array.isArray(wp.support_contacts) ? wp.support_contacts : [],
        });
      })
      .finally(() => setLoading(false));
  }, [programId]);

  const selectedProgram = useMemo(
    () => programs.find((p) => p.program_id === programId),
    [programs, programId]
  );

  const generateWithAI = async () => {
    if (!programId) {
      toast.error("Select a program first");
      return;
    }
    setGenerating(true);
    try {
      const res = await fetch(`${API}/admin/programs/${programId}/welcome-packet/ai-generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          tone,
          cohort_name: cohortName || null,
          start_date: startDate || null,
          instructor_name: instructorName || null,
          audience_notes: audienceNotes || null,
          extra_instructions: extraInstructions || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "AI generation failed");
      // Merge AI draft (preserves existing URLs)
      setDraft((d) => ({
        ...d,
        welcome_letter: data.draft.welcome_letter || d.welcome_letter,
        instructor_intro: data.draft.instructor_intro || d.instructor_intro,
        tech_checklist: data.draft.tech_checklist?.length ? data.draft.tech_checklist : d.tech_checklist,
        key_dates: data.draft.key_dates?.length ? data.draft.key_dates : d.key_dates,
        code_of_conduct: data.draft.code_of_conduct || d.code_of_conduct,
        support_contacts: data.draft.support_contacts?.length ? data.draft.support_contacts : d.support_contacts,
      }));
      toast.success("AI draft loaded — review and Save when ready");
    } catch (err) {
      toast.error(err.message || "AI generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    if (!programId) return;
    setSaving(true);
    try {
      const payload = {
        pdf_url: draft.pdf_url || null,
        video_url: draft.video_url || null,
        welcome_letter: draft.welcome_letter || null,
        instructor_intro: draft.instructor_intro || null,
        tech_checklist: draft.tech_checklist.filter(Boolean),
        key_dates: draft.key_dates.filter((d) => d.label || d.date),
        code_of_conduct: draft.code_of_conduct || null,
        support_contacts: draft.support_contacts.filter((c) => c.label || c.value),
      };
      const res = await fetch(`${API}/admin/programs/${programId}/welcome-packet`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err?.detail || "Save failed");
      }
      toast.success("Welcome packet saved");
    } catch (err) {
      toast.error(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Helpers for arrays
  const updateChecklistItem = (i, v) => setDraft((d) => {
    const next = [...d.tech_checklist]; next[i] = v; return { ...d, tech_checklist: next };
  });
  const removeChecklist = (i) => setDraft((d) => ({ ...d, tech_checklist: d.tech_checklist.filter((_, idx) => idx !== i) }));
  const addChecklist = () => setDraft((d) => ({ ...d, tech_checklist: [...d.tech_checklist, ""] }));

  const updateDate = (i, key, v) => setDraft((d) => {
    const next = [...d.key_dates]; next[i] = { ...next[i], [key]: v }; return { ...d, key_dates: next };
  });
  const removeDate = (i) => setDraft((d) => ({ ...d, key_dates: d.key_dates.filter((_, idx) => idx !== i) }));
  const addDate = () => setDraft((d) => ({ ...d, key_dates: [...d.key_dates, { label: "", date: "" }] }));

  const updateContact = (i, key, v) => setDraft((d) => {
    const next = [...d.support_contacts]; next[i] = { ...next[i], [key]: v }; return { ...d, support_contacts: next };
  });
  const removeContact = (i) => setDraft((d) => ({ ...d, support_contacts: d.support_contacts.filter((_, idx) => idx !== i) }));
  const addContact = () => setDraft((d) => ({ ...d, support_contacts: [...d.support_contacts, { label: "", value: "" }] }));

  return (
    <div className="space-y-5" data-testid="welcome-pack-editor">
      <Card className="border border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-slate-900 flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-[#FFD507]" /> AI Welcome Pack Editor
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            Pick a program, generate a personalised welcome packet with AI, then review and save. Participants see the saved packet at
            <span className="font-mono ml-1">/programs/&lt;program_id&gt;/welcome</span>.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="wp-program">Program</Label>
              <Select value={programId} onValueChange={setProgramId}>
                <SelectTrigger id="wp-program" data-testid="wp-program-select"><SelectValue placeholder="Choose a program" /></SelectTrigger>
                <SelectContent>
                  {programs.map((p) => (
                    <SelectItem key={p.program_id} value={p.program_id}>{p.title || p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedProgram && (
                <a href={`/programs/${selectedProgram.program_id}/welcome`} target="_blank" rel="noopener noreferrer" className="text-xs text-[#1E3A8A] hover:underline mt-1 inline-flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" /> Preview current packet
                </a>
              )}
            </div>
            <div>
              <Label htmlFor="wp-cohort">Cohort name (optional)</Label>
              <Input id="wp-cohort" value={cohortName} onChange={(e) => setCohortName(e.target.value)} placeholder="e.g. Spring 2026 Cohort" data-testid="wp-cohort-input" />
            </div>
            <div>
              <Label htmlFor="wp-start">Start date (optional)</Label>
              <Input id="wp-start" value={startDate} onChange={(e) => setStartDate(e.target.value)} placeholder="e.g. March 4, 2026" data-testid="wp-start-input" />
            </div>
            <div>
              <Label htmlFor="wp-instructor">Instructor name (optional)</Label>
              <Input id="wp-instructor" value={instructorName} onChange={(e) => setInstructorName(e.target.value)} placeholder="e.g. Jamie Patel" data-testid="wp-instructor-input" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="wp-audience">Audience notes (optional)</Label>
              <Input id="wp-audience" value={audienceNotes} onChange={(e) => setAudienceNotes(e.target.value)} placeholder="e.g. first-time learners, mixed technical levels" data-testid="wp-audience-input" />
            </div>
            <div>
              <Label htmlFor="wp-tone">Tone</Label>
              <Input id="wp-tone" value={tone} onChange={(e) => setTone(e.target.value)} data-testid="wp-tone-input" />
            </div>
            <div>
              <Label htmlFor="wp-extra">Extra instructions for AI (optional)</Label>
              <Input id="wp-extra" value={extraInstructions} onChange={(e) => setExtraInstructions(e.target.value)} placeholder="e.g. mention WIOA funding info" data-testid="wp-extra-input" />
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
            <Button onClick={generateWithAI} disabled={generating || !programId} className="bg-[#1E3A8A] hover:bg-[#0F1B3D] text-white" data-testid="wp-ai-generate-btn">
              {generating ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating…</>) : (<><Sparkles className="w-4 h-4 mr-2" /> Generate with AI</>)}
            </Button>
            <Button onClick={save} disabled={saving || !programId} variant="outline" className="border-[#FFD507] text-[#1E3A8A]" data-testid="wp-save-btn">
              {saving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>) : (<><Save className="w-4 h-4 mr-2" /> Save Packet</>)}
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <Card><CardContent className="p-6 text-slate-500">Loading current packet…</CardContent></Card>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          <Card className="border border-slate-200">
            <CardHeader><CardTitle className="text-base text-slate-900">Welcome letter</CardTitle></CardHeader>
            <CardContent>
              <Textarea rows={10} value={draft.welcome_letter} onChange={(e) => setDraft({ ...draft, welcome_letter: e.target.value })} placeholder="A warm 1-2 paragraph note to the cohort." data-testid="wp-letter" />
            </CardContent>
          </Card>

          <Card className="border border-slate-200">
            <CardHeader><CardTitle className="text-base text-slate-900">Instructor intro</CardTitle></CardHeader>
            <CardContent>
              <Textarea rows={10} value={draft.instructor_intro} onChange={(e) => setDraft({ ...draft, instructor_intro: e.target.value })} placeholder="Short bio + what students can expect from the instructor." data-testid="wp-instructor-intro" />
            </CardContent>
          </Card>

          <Card className="border border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base text-slate-900">Tech checklist</CardTitle>
              <Button size="sm" variant="ghost" onClick={addChecklist} data-testid="wp-add-checklist"><Plus className="w-4 h-4 mr-1" />Add</Button>
            </CardHeader>
            <CardContent className="space-y-2">
              {draft.tech_checklist.length === 0 && <p className="text-xs text-slate-400">No items yet.</p>}
              {draft.tech_checklist.map((item, i) => (
                <div key={i} className="flex gap-2">
                  <Input value={item} onChange={(e) => updateChecklistItem(i, e.target.value)} placeholder="e.g. Install Google Chrome" data-testid={`wp-checklist-${i}`} />
                  <Button size="icon" variant="ghost" onClick={() => removeChecklist(i)} data-testid={`wp-checklist-remove-${i}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="border border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base text-slate-900">Key dates</CardTitle>
              <Button size="sm" variant="ghost" onClick={addDate} data-testid="wp-add-date"><Plus className="w-4 h-4 mr-1" />Add</Button>
            </CardHeader>
            <CardContent className="space-y-2">
              {draft.key_dates.length === 0 && <p className="text-xs text-slate-400">No dates yet.</p>}
              {draft.key_dates.map((d, i) => (
                <div key={i} className="flex gap-2">
                  <Input value={d.label} onChange={(e) => updateDate(i, "label", e.target.value)} placeholder="Label (e.g. Orientation)" data-testid={`wp-date-label-${i}`} />
                  <Input value={d.date} onChange={(e) => updateDate(i, "date", e.target.value)} placeholder="Date (e.g. Mar 4, 2026)" data-testid={`wp-date-date-${i}`} />
                  <Button size="icon" variant="ghost" onClick={() => removeDate(i)} data-testid={`wp-date-remove-${i}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="border border-slate-200 lg:col-span-2">
            <CardHeader><CardTitle className="text-base text-slate-900">Code of conduct</CardTitle></CardHeader>
            <CardContent>
              <Textarea rows={6} value={draft.code_of_conduct} onChange={(e) => setDraft({ ...draft, code_of_conduct: e.target.value })} placeholder="Short, plain-language community guidelines." data-testid="wp-coc" />
            </CardContent>
          </Card>

          <Card className="border border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base text-slate-900">Support contacts</CardTitle>
              <Button size="sm" variant="ghost" onClick={addContact} data-testid="wp-add-contact"><Plus className="w-4 h-4 mr-1" />Add</Button>
            </CardHeader>
            <CardContent className="space-y-2">
              {draft.support_contacts.length === 0 && <p className="text-xs text-slate-400">No contacts yet.</p>}
              {draft.support_contacts.map((c, i) => (
                <div key={i} className="flex gap-2">
                  <Input value={c.label} onChange={(e) => updateContact(i, "label", e.target.value)} placeholder="Label (e.g. Email)" data-testid={`wp-contact-label-${i}`} />
                  <Input value={c.value} onChange={(e) => updateContact(i, "value", e.target.value)} placeholder="Value (e.g. academy@aspiritech.org)" data-testid={`wp-contact-value-${i}`} />
                  <Button size="icon" variant="ghost" onClick={() => removeContact(i)} data-testid={`wp-contact-remove-${i}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="border border-slate-200">
            <CardHeader><CardTitle className="text-base text-slate-900">Attachments (optional)</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label htmlFor="wp-pdf">PDF URL</Label>
                <Input id="wp-pdf" value={draft.pdf_url} onChange={(e) => setDraft({ ...draft, pdf_url: e.target.value })} placeholder="https://…" data-testid="wp-pdf-url" />
              </div>
              <div>
                <Label htmlFor="wp-video">Welcome video URL</Label>
                <Input id="wp-video" value={draft.video_url} onChange={(e) => setDraft({ ...draft, video_url: e.target.value })} placeholder="https://…" data-testid="wp-video-url" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default WelcomePackEditor;
